import { SetStateAction } from "react";
import { SECURITY_CODE_TO_COINBASE_ID } from "api/securities";
import {
  SecurityCode,
  SecurityCodeWithoutTransferSupport,
} from "api/securities";
import { LIMIT_ORDER_ONLY_SECURITIES } from "api/securities";
import { TOptions, StringMap } from "i18next";
import { getDecimalPlaces } from "views/trading/utils";

/**
 * Return the number of decimal places that should be shown for a quote,
 * calculated directly from the price string.
 */
export const getAllDecimalPlaces = (
  numOrCode: number | string | undefined
): number => {
  if (numOrCode === undefined || numOrCode === null) {
    return 4;
  }

  // Convert to string to handle both number and string inputs
  const priceStr = numOrCode.toString();

  // Split on decimal point and get the decimal part
  const [, dec = ""] = priceStr.split(".");

  // Return the length of decimal places, or 4 as default
  return dec.length || 4;
};

export const validateUnitsTrade = (
  inputValue: string,
  setError: (value: SetStateAction<string>) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: TOptions<StringMap> | undefined) => any,
  minMaxData?: {
    minimumSize: number;
    minSizeIncrement: number;
    maximumSize: number;
  }
) => {
  const value = parseFloat(inputValue);
  const minDecimalPlaces = getDecimalPlaces(minMaxData?.minSizeIncrement);
  const inputDecimalPlaces = getDecimalPlaces(parseFloat(inputValue));

  if (
    isNaN(value) || // should be a number
    !minMaxData?.minimumSize || // must be defined
    !minMaxData?.minimumSize || // must be defined
    inputDecimalPlaces > minDecimalPlaces // too many decimal places in input
  ) {
    setError(
      t("tradingModal.minBuyAmountError", {
        // Using this as a generic error
        minBuyAmount: minMaxData?.minimumSize,
        decimalPlaces: minDecimalPlaces,
      })
    );
    return;
  }

  // value too small
  if (value < minMaxData?.minimumSize) {
    setError(
      t("tradingModal.minBuyAmountError", {
        minBuyAmount: minMaxData?.minimumSize,
        decimalPlaces: minDecimalPlaces,
      })
    );
    // value too large
  } else if (value > minMaxData?.maximumSize) {
    setError(
      t("tradingModal.maxBuyAmountError", {
        maxBuyAmount: minMaxData?.maximumSize,
        decimalPlaces: minDecimalPlaces,
      })
    );
  } else {
    setError("");
  }
  return;
};

export const validateAmountTrade = (
  inputValue: string,
  setError: (value: React.SetStateAction<string>) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: TOptions<StringMap> | undefined) => any,
  unitPrice: number,
  minMaxData?: { minimumSize: number; maximumSize: number }
) => {
  const value = parseFloat(inputValue);

  const unitsFromAmount = value / unitPrice;

  const minDecimalPlaces = (
    minMaxData?.minimumSize.toString().split(".")[1] || ""
  ).length;
  const inputDecimalPlaces = (inputValue.split(".")[1] || "").length;

  if (
    isNaN(value) || // inputValue must be a number
    value <= 0 || // inputValue must be positive
    !minMaxData ||
    !minMaxData?.minimumSize || // Min must be defined
    !minMaxData?.maximumSize || // Max must be defined
    isNaN(unitsFromAmount) || // result of division must be a number
    inputDecimalPlaces > minDecimalPlaces // too many decimal places in input
  ) {
    setError(
      t("tradingModal.invalidTradeAmountError", {
        // You might need to define this key in your translations
        minAmount: minMaxData?.minimumSize ?? 0 * unitPrice,
        maxAmount: minMaxData?.maximumSize ?? 0 * unitPrice,
        decimalPlaces: minDecimalPlaces,
      })
    );
    return;
  }

  // The computed units from the amount are too small
  if (unitsFromAmount < minMaxData.minimumSize) {
    setError(
      t("tradingModal.minTradeAmountError", {
        // Define or adjust this key in your translations
        minAmount: minMaxData.minimumSize * unitPrice,
        decimalPlaces: minDecimalPlaces,
      })
    );
  } else if (unitsFromAmount > minMaxData.maximumSize) {
    // The computed units from the amount are too large
    setError(
      t("tradingModal.maxTradeAmountError", {
        // Define or adjust this key in your translations
        maxAmount: minMaxData.maximumSize * unitPrice,
        decimalPlaces: minDecimalPlaces,
      })
    );
  } else {
    setError("");
  }
};

export const getCoinbaseName = (name: string): string => {
  return SECURITY_CODE_TO_COINBASE_ID[
    name as keyof typeof SECURITY_CODE_TO_COINBASE_ID
  ];
};
export const isLimitOrderOnly = (
  securityCode: SecurityCode | SecurityCodeWithoutTransferSupport
): boolean => {
  return LIMIT_ORDER_ONLY_SECURITIES.has(securityCode);
};

export const formatNumberForDisplay = (num: number): string => {
  if (!Number.isFinite(num)) {
    // Handle NaN, Infinity, -Infinity
    return "-";
  }

  // Handle 0 specifically to ensure it becomes "0.0"
  if (num === 0) {
    return "0.0";
  }

  const absNum = Math.abs(num);
  let formattedString: string;
  const isInteger = num % 1 === 0;

  if (absNum >= 1) {
    // For numbers >= 1 (or <= -1), show 2 decimal places initially
    formattedString = num.toFixed(2);
  } else {
    // For numbers between -1 and 1 (excluding 0)
    // Calculate the position of the first significant digit
    const p = Math.ceil(-Math.log10(absNum));
    // Show 2 digits *after* the first non-zero digit
    // Cap decimals for very small numbers, ensure at least 1 for numbers like 0.999...
    const decimalPlaces = Math.max(1, Math.min(10, p + 1));
    formattedString = num.toFixed(decimalPlaces);
  }

  // Strip trailing zeros
  if (formattedString.includes(".")) {
    let trimmed = formattedString.replace(/0+$/, ""); // Remove trailing zeros e.g. "1.0100" -> "1.01"
    trimmed = trimmed.replace(/\.$/, ""); // Remove trailing decimal point if applicable e.g. "123." -> "123"

    // Exception: If the original number was an integer (like 1.0 or 123)
    // and trimming removed the decimal point entirely, put back ".0"
    if (isInteger && !trimmed.includes(".")) {
      // Handles 1 -> "1.0", -1 -> "-1.0", 0 -> "0.0" (via initial check), -0 -> "0.0" (via initial check)
      return trimmed + ".0";
    }

    // Handle cases where trimming results in "0" or "-0" for non-integer inputs
    // (e.g., very small numbers rounding to 0.0000)
    if ((trimmed === "0" || trimmed === "-0") && !isInteger) {
      return "0.0";
    }

    return trimmed; // Return the potentially trimmed string e.g., "1.01", "0.01235", "-1.0"
  } else {
    // Fallback for non-decimal strings (shouldn't happen often with toFixed)
    if (isInteger) {
      return formattedString + ".0"; // Ensure integers get ".0"
    }
    return formattedString;
  }
};
